import {
  CANDIDATE_NOUN,
  formatCount,
  formatDateDE,
  jobPlaceLine,
  nounFor,
  userInitials,
  type JobSummary,
  type User,
} from "@vidario/domain";
import { Avatar, Card, DropdownMenu, JobStatusBadge, ProcessStepper } from "@vidario/ui";
import { ArrowRight, Calendar, Lock, MapPin, MoreHorizontal } from "lucide-react";
import { type MouseEvent, type ReactElement } from "react";

export interface JobCardProps {
  job: JobSummary;
  /** Resolved for admin view — shows the creator's avatar + name on the
   * card. Omit for recruiter view. */
  creator?: User | undefined;
  isAdmin: boolean;
  onOpen: () => void;
  onCloseJob?: () => void;
}

/** Dashboard job card. Shows title/company, location + created date,
 * ProcessStepper driven by `currentStep`, candidate count, and either a
 * recruiter avatar (admin) or a subtle "Öffnen →" affordance (recruiter).
 * The kebab menu exposes "Stelle schließen" (MVP_requirements.md §5.1). */
export function JobCard({ job, creator, isAdmin, onOpen, onCloseJob }: JobCardProps): ReactElement {
  const stopPropagation = (e: MouseEvent): void => {
    e.stopPropagation();
  };

  return (
    <Card
      interactive
      onClick={onOpen}
      className="flex h-full flex-col gap-4"
      data-testid={`job-card-${job.id}`}
    >
      {/* On phones the status and menu get their own row above the title, so the title keeps the full width. */}
      <div className="flex items-start justify-between gap-3 max-sm:flex-col-reverse">
        <div className="min-w-0 flex-1 max-sm:self-stretch">
          <h3 className="m-0 text-xl font-semibold leading-tight -tracking-[0.3px] text-text-primary wrap-anywhere">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
              className="inline cursor-pointer bg-transparent p-0 text-left font-inherit text-inherit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-0"
              aria-label={`Stelle öffnen: ${job.title}`}
            >
              {job.title}
            </button>
          </h3>
          <div className="mt-1 text-sm text-text-secondary wrap-anywhere">{job.company}</div>
        </div>
        <div className="flex items-center gap-2 max-sm:self-stretch max-sm:justify-between">
          <JobStatusBadge status={job.status} />
          {onCloseJob && job.status !== "closed" && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild onClick={stopPropagation}>
                <button
                  type="button"
                  aria-label="Stelle-Menü"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  <MoreHorizontal size={16} />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content onClick={stopPropagation}>
                  <DropdownMenu.Item onSelect={() => onCloseJob()}>
                    <Lock size={14} /> Stelle schließen
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[13px] text-text-tertiary">
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <MapPin size={14} className="shrink-0" />{" "}
          {jobPlaceLine(job.location, job.workModel, { short: true })}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Calendar size={14} /> {formatDateDE(job.createdAt)}
        </span>
      </div>

      <div className="mt-auto border-t border-neutral-100 pt-4">
        <ProcessStepper currentStep={job.currentStep} size="sm" />
      </div>

      <div className="flex items-center justify-between pt-1.5">
        <div className="text-[13px] font-medium text-text-secondary">
          <span className="text-xl font-bold -tracking-[0.3px] text-text-primary">
            {formatCount(job.candidateCount)}
          </span>{" "}
          <span className="text-text-tertiary">{nounFor(job.candidateCount, CANDIDATE_NOUN)}</span>
        </div>
        {isAdmin && creator ? (
          <div className="inline-flex items-center gap-2 text-xs text-text-tertiary">
            <Avatar initials={userInitials(creator.name)} size={22} />
            <span>{creator.name}</span>
          </div>
        ) : (
          <div className="inline-flex items-center gap-1 text-[13px] font-medium text-primary-600">
            Öffnen <ArrowRight size={14} />
          </div>
        )}
      </div>
    </Card>
  );
}
